import React from 'react';
import FreeResiliationFormulaContext from './FreeResiliationFormulaContext';
import computeCurrentOfferCost from './computations/computeCurrentOfferCost';
import computeNewOfferCost from './computations/computeNewOfferCost';
import computeSavings from './computations/computeSavings';
import computeTerminationFees from './computations/computeTerminationFees';
import { ResiliationFormulaState } from './types';
import computeCompletedSteps from './utils/computeCompletedSteps';

export default function FreeResiliationFormulaContextProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [state, setState] = React.useState<ResiliationFormulaState>({});
  const [expanded, setExpanded] = React.useState<string | null>('contract');

  const terminationFees = React.useMemo(() => computeTerminationFees(state), [state]);
  const currentOfferCost = React.useMemo(() => computeCurrentOfferCost(state), [state]);
  const newOfferCost = React.useMemo(() => computeNewOfferCost(state), [state]);
  const savings = React.useMemo(
    () => computeSavings(terminationFees, currentOfferCost, newOfferCost),
    [terminationFees, currentOfferCost, newOfferCost],
  );

  const completedSteps = React.useMemo(() => computeCompletedSteps(state), [state]);
  const previousCompletedSteps = React.useRef(completedSteps);

  React.useEffect(() => {
    if (completedSteps.contract && !previousCompletedSteps.current.contract) {
      setExpanded('commitment');
      previousCompletedSteps.current = completedSteps;
      return;
    }
    if (completedSteps.commitment && !previousCompletedSteps.current.commitment) {
      setExpanded('price');
      previousCompletedSteps.current = completedSteps;
      return;
    }
    previousCompletedSteps.current = completedSteps;
  }, [completedSteps]);

  const value = React.useMemo(() => ({
    state,
    updateState: setState,
    clear() {
      setState({});
      setExpanded(null);
    },
    terminationFees,
    currentOfferCost,
    newOfferCost,
    savings,
    completedSteps,
    expanded,
    setExpanded,
  }), [state, terminationFees, currentOfferCost, newOfferCost, savings, completedSteps, expanded, setExpanded]);

  return (
    <FreeResiliationFormulaContext.Provider value={value}>
      {children}
    </FreeResiliationFormulaContext.Provider>
  );
}
